import BugsnagPerformance, { DefaultRoutingProvider } from '@bugsnag/browser-performance';
import Bugsnag from '@bugsnag/js';
import BugsnagPluginReact from '@bugsnag/plugin-react';
import { publicEnvConfig } from 'helpers/env';
import { isServerSide } from 'helpers/isServerSide';
import React from 'react';
import handledErrors, { isMinifiedReactError, regexHandledErrors } from './handledErrors';
import { isSafariBrowser } from 'helpers/browser';

const ARCHIE_MEDIA_QUERIES = {
	desktop: '(min-width: 993px)',
	smallDesktop: '(min-width: 769px) and (max-width: 992px)',
	tablet: '(min-width: 481px) and (max-width: 768px)',
	mobile: '(max-width: 481px)',
};
const VDP_HYDRATION_BOUNDARIES = [
	'vdp-top-action-bar',
	'vdp-carousel',
	'vdp-vehicle-info',
	'vdp-condition-report',
	'vdp-offer-bar',
];

const HYDRATION_REACT_INVARIANTS = ['418', '423'];

export function resolveRoute(url) {
	const pathname = url.pathname;
	if (pathname.match(/\/vehicle_detail\/\d+/i)) {
		return '/vehicle_detail/:id';
	}

	if (pathname.match(/\/condition-report\/\w+/i)) {
		return '/condition-report/:id';
	}

	if (pathname.match(/\/webapp\/gtv_offers\/[0-9a-f-]+\/redemption_verification$/i)) {
		return '/webapp/gtv_offers/:offerId/redemption_verification';
	}

	if (pathname.match(/\/webapp\/gtv_offers\/[0-9a-f-]+\/redemption_authentication$/i)) {
		return '/webapp/gtv_offers/:offerId/redemption_authentication';
	}

	if (pathname.startsWith('/en') || pathname.startsWith('/es')) {
		return pathname.replace(/^\/(en|es)/, '');
	}

	return pathname;
}

function initializeBugsnagClient() {
	const bugsnagApiKey =
		isServerSide() ? publicEnvConfig.BUGSNAG_SERVER_API_KEY : publicEnvConfig.BUGSNAG_BROWSER_API_KEY;

	if (publicEnvConfig.BUGSNAG_ENABLED === false) return;

	const releaseStage = publicEnvConfig.RELEASE_STAGE || 'development';

	// During build time, we dont have any keys, we are using the bugsnag public server key as fallback
	const fallbackServerBugsnagKey = 'cac0f7d5b6491b7dbaa092cc3584b615';
	const bugsnagConfig = {
		apiKey: bugsnagApiKey || fallbackServerBugsnagKey,
		releaseStage: releaseStage,
		metadata: {},
		appVersion: publicEnvConfig.VERSION || 'development',
		onError: onError,
		// user: loggedUSerSelector(getState()),
	};

	// If it is already started it wont start again
	Bugsnag.start({ ...bugsnagConfig, plugins: [new BugsnagPluginReact(React)] });

	if (!isServerSide()) {
		BugsnagPerformance.start({
			...bugsnagConfig,
			routingProvider: new DefaultRoutingProvider(resolveRoute),
			autoInstrumentNetworkRequests: false,
			settleIgnoreUrls: [/\/_next\/.*/],
		});
	}
}

const isAbortError = (event) => {
	try {
		return event.errors.some((e) => e.errorClass === 'AbortError');
	} catch {
		return false;
	}
};

const getMessageFromError = (error) => {
	const { message, errorMessage } = error;
	return message || errorMessage;
};

const getPrimaryErrorMessage = (event) => {
	try {
		return getMessageFromError(event.errors[0]);
	} catch {
		return null;
	}
};

const isInlineScriptError = (event) => {
	try {
		const isInline = event.errors.some((error) => error.stacktrace.some((frame) => frame.file === '<inline>'));
		return isInline;
	} catch {
		return false;
	}
};

const isErrorOnHandledList = (event) => {
	if (event.errors) {
		for (const error of event.errors) {
			const message = getMessageFromError(error);
			if (handledErrors.some((handledMessage) => message?.includes(handledMessage))) return true;
			if (regexHandledErrors.some((regex) => regex.test(message))) {
				return true;
			}
		}
	}

	return false;
};

const getReactInvariantFromMessage = (errorMessage) => {
	if (!errorMessage) return null;

	const queryInvariantMatch = errorMessage.match(/invariant=(\d+)/i);
	if (queryInvariantMatch?.[1]) return queryInvariantMatch[1];

	const hashInvariantMatch = errorMessage.match(/error #(\d+)/i);
	return hashInvariantMatch?.[1] || null;
};

const getMediaMatch = (query) => {
	try {
		return window.matchMedia(query).matches;
	} catch {
		return null;
	}
};

const getArchieMediaFlags = () => {
	if (typeof window === 'undefined') return null;

	return {
		isDesktop: getMediaMatch(ARCHIE_MEDIA_QUERIES.desktop),
		isSmallDesktop: getMediaMatch(ARCHIE_MEDIA_QUERIES.smallDesktop),
		isTablet: getMediaMatch(ARCHIE_MEDIA_QUERIES.tablet),
		isMobile: getMediaMatch(ARCHIE_MEDIA_QUERIES.mobile),
	};
};

const getVehicleLayoutStorageSnapshot = () => {
	try {
		const layoutKeys = Object.keys(window.localStorage)
			.filter((key) => key.includes('vehicleLayout'))
			.slice(0, 10);
		return layoutKeys.map((key) => ({ key, value: window.localStorage.getItem(key) }));
	} catch {
		return [];
	}
};

const getHydrationBoundarySnapshot = () => {
	if (typeof window === 'undefined' || typeof document === 'undefined') return null;

	const foundBoundaries = VDP_HYDRATION_BOUNDARIES.filter((name) =>
		Boolean(document.querySelector(`[data-rh-boundary="${name}"]`)),
	);
	const missingBoundaries = VDP_HYDRATION_BOUNDARIES.filter((name) => !foundBoundaries.includes(name));

	const hydrationDebugMarkers = Array.from(document.querySelectorAll('[data-rh-debug]')).map((markerElement) => {
		const markerName = markerElement.getAttribute('data-rh-debug');
		const parentBoundary = markerElement.closest('[data-rh-boundary]');
		const boundary = parentBoundary?.getAttribute('data-rh-boundary') || null;

		return {
			marker: markerName,
			boundary,
		};
	});

	const debugMarkerBoundaries = [...new Set(hydrationDebugMarkers.map((marker) => marker.boundary).filter(Boolean))];
	const suspectedBoundary =
		debugMarkerBoundaries.length === 1 ? debugMarkerBoundaries[0]
		: missingBoundaries.length === 1 ? missingBoundaries[0]
		: null;

	return {
		hasVdpBody: Boolean(document.querySelector('#vehicle-details-body')),
		foundBoundaries,
		missingBoundaries,
		boundaryCount: foundBoundaries.length,
		hydrationDebugMarkers,
		suspectedBoundary,
	};
};

const isHydrationInvariantMessage = (message) => {
	if (!message) return false;

	const reactInvariant = getReactInvariantFromMessage(message);
	if (reactInvariant) {
		return HYDRATION_REACT_INVARIANTS.includes(reactInvariant);
	}
	const lowerMessage = message.toLowerCase();

	return (
		lowerMessage.includes('hydration failed') ||
		lowerMessage.includes('did not match') ||
		lowerMessage.includes('text content does not match') ||
		lowerMessage.includes('server html contained') ||
		lowerMessage.includes('there was an error while hydrating')
	);
};

const addMinifiedReactDebugMetadata = (event, primaryErrorMessage) => {
	if (typeof window === 'undefined' || typeof event?.addMetadata !== 'function') return;

	const reactInvariant = getReactInvariantFromMessage(primaryErrorMessage);
	if (reactInvariant && !HYDRATION_REACT_INVARIANTS.includes(reactInvariant)) return;

	try {
		const currentUrl = new URL(window.location.href);
		const searchParams = currentUrl.searchParams;
		const partnerName = searchParams.get('partner_name');
		const enablePartnerNavigation = searchParams.get('enable_partner_navigation');
		const disablePartnerScript = searchParams.get('disable_partner_script');
		const vehicleId = searchParams.get('vehicleid') || currentUrl.pathname.split('/').pop();

		event.addMetadata('reactHydrationDebug', {
			reactInvariant: reactInvariant || 'unknown',
			releaseStage: publicEnvConfig.RELEASE_STAGE || null,
			route: {
				pathname: currentUrl.pathname,
				search: currentUrl.search,
			},
			query: {
				vehicleid: vehicleId,
				controller: searchParams.get('controller'),
				partner_name: partnerName,
				enable_partner_navigation: enablePartnerNavigation,
				disable_partner_script: disablePartnerScript,
				utm_source: searchParams.get('utm_source'),
				utm_medium: searchParams.get('utm_medium'),
				utm_campaign: searchParams.get('utm_campaign'),
				utm_content: searchParams.get('utm_content'),
			},
			partnerFlags: {
				isStockwavePartner: partnerName === 'stockwave',
				enablePartnerNavigation: enablePartnerNavigation === 'true',
				disablePartnerScript: disablePartnerScript === 'true',
			},
			viewport: {
				width: window.innerWidth,
				height: window.innerHeight,
				devicePixelRatio: window.devicePixelRatio || null,
			},
			mediaFlags: getArchieMediaFlags(),
			vehicleLayoutLocalStorage: getVehicleLayoutStorageSnapshot(),
			userAgent: window.navigator?.userAgent || null,
		});
	} catch {}
};

export const onError = (event) => {
	let primaryErrorMessage = getPrimaryErrorMessage(event);
	const minifiedReactError = isMinifiedReactError(primaryErrorMessage);
	const hydrationInvariant = isHydrationInvariantMessage(primaryErrorMessage);

	if (minifiedReactError) {
		addMinifiedReactDebugMetadata(event, primaryErrorMessage);
	}

	if (hydrationInvariant && typeof event?.addMetadata === 'function') {
		const boundarySnapshot = getHydrationBoundarySnapshot();
		if (boundarySnapshot) {
			event.addMetadata('reactHydrationBoundaryDebug', boundarySnapshot);
		}
	}

	if (
		isErrorOnHandledList(event) ||
		minifiedReactError ||
		(isSafariBrowser() && isAbortError(event)) ||
		isInlineScriptError(event)
	) {
		event.unhandled = false;
	}
};

export default initializeBugsnagClient;
